import React, { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import {
  get,
  has,
  getOr,
  omit,
  isEmpty,
  forEach,
  upperFirst,
  includes
} from 'lodash/fp';
import { useForm } from 'react-hook-form';
import { useStoreActions, useStoreState } from 'easy-peasy';
import {
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  InputLabel,
  InputAdornment,
  FormHelperText
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import useStyles from '../../../provider/styles/form';
import { reservationOrder as reservationOrderPath } from '../../../paths';
import history from '../../../history';
import UserDetailsType from '../../../interfaces/UserDetailsType';
import { getCurrentUser } from '../../../provider/currentUser';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import { LOGIN, CONTACT } from '../../../constants/UserEmailTypes';
import STATUS_ACTIONS from '../../../constants/ReservationStatusActions';
import useLocationQuery from '../../../hooks/useLocationQuery';
import useLoadReservation from '../../../hooks/useLoadReservation';
import EmailIconWithVerifiedBadge from '../../../components/EmailIconWithVerifiedBadge';
import VerifyEmailButton from '../../../components/VerifyEmailButton';
import Loading from '../../../components/Loading';
import { parseInt } from '../../../helpers/utils';

const forEachWithKey = get('convert', forEach)({ cap: false });

const createAddressValidation = () => ({
  is: '0',
  then: yup.string().required('Required field'),
  otherwise: yup.string().notRequired()
});

const createContactNumberValidation = fieldSetting => {
  const validation = yup
    .string()
    .test('len', 'Maximum of 20 characters', ({ length }) => length <= 20);

  return getOr(false, 'isRequired', fieldSetting)
    ? validation.required('Required field')
    : validation;
};

const createValidationSchema = fieldsSettings => {
  const mainFields = {
    firstName: yup.string().required('Required field'),
    lastName: yup.string().required('Required field'),
    homeless: yup.string().required('Required field'),
    loginEmail: yup
      .string()
      .email('Must be a valid email')
      .required('Required field'),
    householdTotal: yup
      .number()
      .integer()
      .required('Required field')
      .typeError('Required field')
      .min(1)
      .max(50)
  };

  const addressFields = {
    streetAddress: getOr(false, 'streetAddress.isRequired', fieldsSettings)
      ? yup.string().when('homeless', createAddressValidation())
      : yup.string(),
    apartment: yup.string(),
    city: getOr(false, 'city.isRequired', fieldsSettings)
      ? yup.string().when('homeless', createAddressValidation())
      : yup.string(),
    state: getOr(false, 'state.isRequired', fieldsSettings)
      ? yup.string().when('homeless', createAddressValidation())
      : yup.string(),
    zipCode: (getOr(false, 'zipCode.isRequired', fieldsSettings)
      ? yup.string().when('homeless', createAddressValidation())
      : yup.string()
    ).matches(/^[a-zA-Z0-9 ]{1,9}$/, {
      message:
        'Only accepts numbers, letters and spaces - maximum of 9 characters',
      excludeEmptyString: true
    }),
    county: getOr(false, 'county.isRequired', fieldsSettings)
      ? yup.string().required('Required field')
      : yup.string()
  };

  const contactFields = {
    email: yup
      .string()
      .email('Must be a valid email')
      .required('Required field'),
    homePhone: createContactNumberValidation(get('homePhone', fieldsSettings)),
    workPhone: createContactNumberValidation(get('workPhone', fieldsSettings)),
    cellPhone: createContactNumberValidation(get('cellPhone', fieldsSettings))
  };

  return yup
    .object()
    .shape({ ...mainFields, ...addressFields, ...contactFields });
};

const composeDefaultFormValue = ({ loginEmail, userDetails }) => ({
  loginEmail,
  email: getOr(loginEmail, 'email', userDetails),
  firstName: getOr('', 'firstName', userDetails),
  lastName: getOr('', 'lastName', userDetails),
  ...getOr({}, 'otherInfo', userDetails)
});

const fieldServerError = (field, error = {}) => {
  const serverError = getOr('', 'types.message', error);
  const replacePrefix = includes(field, ['email', 'loginEmail'])
    ? `Field ${field} is `
    : `Field ${field} `;
  return upperFirst(serverError.replace(replacePrefix, ''));
};

const Form = ({ userDetails }) => {
  const classes = useStyles();
  const userSession = getCurrentUser();
  const query = useLocationQuery();
  const { pantryPath = '' } = query;
  const reservationId = has('reservationId', query)
    ? parseInt(query.reservationId)
    : null;
  const [loading, setLoading] = useState(false);
  const {
    hasMininumDetails: reservationHasMininumDetails,
    hasProgramPicklists: reservationHasProgramPicklists
  } = useLoadReservation(reservationId);

  const userDetailsError = useStoreState(get('userDetails.error'));
  const fieldsSettings = useStoreState(
    get('pantryReservationFieldsSettings.data')
  );
  const updateUserDetails = useStoreActions(get('userDetails.update'));
  const updateReservation = useStoreActions(get('reservation.update'));
  const updateStatus = useStoreActions(get('reservation.updateStatus'));
  const { showSuccess } = useStoreActions(get('notifications'));

  const loginEmail = get('email', userSession);
  const submitButtonLabel = reservationId ? 'Continue' : 'Submit';
  const defaultValues = useMemo(
    () => composeDefaultFormValue({ loginEmail, userDetails }),
    [userDetails]
  );

  const homeless = getOr('0', 'otherInfo.homeless', userDetails);
  const defaultHomeless = useMemo(() => homeless, []);
  const isVerifiedLoginEmail = !!get('verifiedAt', userSession);
  const isVerifiedContactEmail = !!get('verifiedAt', userDetails);
  const readOnlyEmails = !!reservationId;
  const validationSchema = createValidationSchema(fieldsSettings);
  const { register, handleSubmit, watch, errors, setError, reset } = useForm({
    validationSchema,
    defaultValues
  });

  const showDetailsUpdatedMessage =
    reservationId && !reservationHasMininumDetails && isEmpty(errors);

  useEffect(() => {
    reset();
  }, []);

  useEffect(() => {
    if (defaultValues && fieldsSettings) {
      forEachWithKey(({ show, isRequired }, field) => {
        if (show && isRequired && !get(field, defaultValues)) {
          setError(field, { type: 'required', message: 'Required field' });
        }
      }, fieldsSettings);
    }
  }, [defaultValues, fieldsSettings]);

  useEffect(() => {
    const formError = get('error', userDetailsError);
    if (!isEmpty(formError)) {
      forEach(({ field, message }) => {
        setError(field, { type: 'serverError', message });
      }, formError);
    }
  }, [userDetailsError]);

  const onSubmit = async formData => {
    setLoading(true);
    const result = await updateUserDetails({
      id: userDetails.id,
      body: formData
    });

    if (get('status', result) !== 'error') {
      if (pantryPath && reservationId) {
        const postData = {
          pantryPath,
          id: reservationId,
          body: omit('loginEmail', formData)
        };
        await updateReservation(postData);

        if (reservationHasProgramPicklists) {
          const pathParams = { pantryPath, id: reservationId };
          history.push(pagePathToUrl(reservationOrderPath, pathParams));
        } else {
          await updateStatus({
            id: reservationId,
            pantryPath,
            statusAction: STATUS_ACTIONS.PENDING
          });
        }
      } else {
        showSuccess({ message: 'Profile successfully updated!' });
      }
    }
    setLoading(false);
  };

  const isError = field => has(field, errors);
  const getFieldError = field => {
    const fieldError = get(field, errors);
    return get('message', fieldError) || fieldServerError(field, fieldError);
  };

  return pantryPath && !fieldsSettings ? (
    <Loading />
  ) : (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      {showDetailsUpdatedMessage && (
        <Alert severity="info" style={{ marginTop: -10, marginBottom: 30 }}>
          Guest Details has been recently updated. Kindly review your details
          and click &quot;Continue&quot; to proceed.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            name="loginEmail"
            label="Login Email"
            variant="outlined"
            inputRef={register}
            error={isError('loginEmail')}
            helperText={getFieldError('loginEmail')}
            InputProps={{
              readOnly: readOnlyEmails,
              startAdornment: (
                <InputAdornment position="start">
                  {!readOnlyEmails && (
                    <EmailIconWithVerifiedBadge
                      verified={isVerifiedLoginEmail}
                    />
                  )}
                </InputAdornment>
              ),
              endAdornment: !isVerifiedLoginEmail && (
                <InputAdornment position="end">
                  {readOnlyEmails ? (
                    'read only'
                  ) : (
                    <VerifyEmailButton label="Verify Email" type={LOGIN} />
                  )}
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            name="email"
            label="Contact Email"
            variant="outlined"
            inputRef={register}
            error={isError('email')}
            helperText={getFieldError('email')}
            InputProps={{
              readOnly: readOnlyEmails,
              startAdornment: (
                <InputAdornment position="start">
                  {!readOnlyEmails && (
                    <EmailIconWithVerifiedBadge
                      verified={isVerifiedContactEmail}
                    />
                  )}
                </InputAdornment>
              ),
              endAdornment: !isVerifiedContactEmail && (
                <InputAdornment position="end">
                  {readOnlyEmails ? (
                    'read only'
                  ) : (
                    <VerifyEmailButton label="Verify Email" type={CONTACT} />
                  )}
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            name="firstName"
            label="First Name"
            variant="outlined"
            inputRef={register}
            error={isError('firstName')}
            helperText={getFieldError('firstName')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            name="lastName"
            label="Last Name"
            variant="outlined"
            inputRef={register}
            error={isError('lastName')}
            helperText={getFieldError('lastName')}
          />
        </Grid>
        <Grid item xs={12}>
          <Box marginTop={1} marginBottom={-1}>
            <RadioGroup
              row
              aria-label="position"
              defaultValue={defaultHomeless}
            >
              <InputLabel
                style={{
                  marginTop: '12px',
                  fontWeight: 'bold',
                  marginRight: '20px'
                }}
              >
                Do you have a physical address?
              </InputLabel>
              <FormControlLabel
                value="0"
                label="Yes"
                control={
                  <Radio name="homeless" color="primary" inputRef={register} />
                }
              />
              <FormControlLabel
                value="1"
                label="No"
                control={
                  <Radio name="homeless" color="primary" inputRef={register} />
                }
              />
            </RadioGroup>
          </Box>
        </Grid>
        {watch('homeless') !== '1' && (
          <>
            <Grid item xs={12}>
              <TextField
                required={getOr(
                  false,
                  'streetAddress.isRequired',
                  fieldsSettings
                )}
                fullWidth
                name="streetAddress"
                label="Street Address"
                variant="outlined"
                inputRef={register}
                error={isError('streetAddress')}
                helperText={getFieldError('streetAddress')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="apartment"
                label="Apartment/Suite"
                variant="outlined"
                inputRef={register}
                error={isError('apartment')}
                helperText={getFieldError('apartment')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required={getOr(false, 'city.isRequired', fieldsSettings)}
                fullWidth
                name="city"
                label="City/Town"
                variant="outlined"
                inputRef={register}
                error={isError('city')}
                helperText={getFieldError('city')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required={getOr(false, 'state.isRequired', fieldsSettings)}
                fullWidth
                name="state"
                label="State/Province"
                variant="outlined"
                inputRef={register}
                error={isError('state')}
                helperText={getFieldError('state')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required={getOr(false, 'zipCode.isRequired', fieldsSettings)}
                fullWidth
                name="zipCode"
                label="Zip/Postal Code"
                variant="outlined"
                inputRef={register}
                error={isError('zipCode')}
                helperText={getFieldError('zipCode')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required={getOr(false, 'county.isRequired', fieldsSettings)}
                fullWidth
                name="county"
                label="County"
                variant="outlined"
                inputRef={register}
                error={isError('county')}
                helperText={getFieldError('county')}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Box marginTop={2}>
            <InputLabel>
              <strong>Contact Numbers</strong>&nbsp;
            </InputLabel>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required={getOr(false, 'homePhone.isRequired', fieldsSettings)}
            type="tel"
            name="homePhone"
            label="Home Phone"
            variant="outlined"
            inputRef={register}
            error={isError('homePhone')}
            helperText={getFieldError('homePhone')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required={getOr(false, 'workPhone.isRequired', fieldsSettings)}
            type="tel"
            name="workPhone"
            label="Work Phone"
            variant="outlined"
            inputRef={register}
            error={isError('workPhone')}
            helperText={getFieldError('workPhone')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required={getOr(false, 'cellPhone.isRequired', fieldsSettings)}
            type="tel"
            name="cellPhone"
            label="Cell Phone"
            variant="outlined"
            inputRef={register}
            error={isError('cellPhone')}
            helperText={getFieldError('cellPhone')}
          />
        </Grid>
        <Grid item xs={12}>
          <Box marginTop={2}>
            <InputLabel>
              <strong>Household Information</strong>
            </InputLabel>
          </Box>
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            required={getOr(false, 'householdTotal.isRequired', fieldsSettings)}
            fullWidth
            min="1"
            max="100"
            type="number"
            variant="outlined"
            name="householdTotal"
            label="How many people in your household?"
            inputRef={register}
            error={isError('householdTotal')}
            helperText={getFieldError('householdTotal')}
          />
          <FormHelperText>Include yourself in the total</FormHelperText>
        </Grid>
      </Grid>
      <Grid container justifyContent="flex-end">
        <Button
          fullWidth
          type="submit"
          color="primary"
          variant="contained"
          className={classes.submit}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            submitButtonLabel
          )}
        </Button>
      </Grid>
    </form>
  );
};

Form.propTypes = {
  userDetails: UserDetailsType
};

Form.defaultProps = {
  userDetails: {}
};

export default Form;
